import React from "react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { requestLiveOtp, verifyLiveOtp, formatApiErrorDetail } from "@/lib/auth_api";
import { toast } from "sonner";

export const ConfirmStopDialog = ({ open, onOpenChange, onConfirm }) => (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent className="panel" data-testid="confirm-stop-dialog">
            <AlertDialogHeader>
                <AlertDialogTitle>Stop the bot?</AlertDialogTitle>
                <AlertDialogDescription>
                    The bot will gracefully stop and disconnect from AsterDex.
                    Open orders will remain on the exchange (in LIVE mode) or in the
                    paper exchange. State is preserved in MongoDB and the bot can be
                    resumed at any time.
                </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogCancel data-testid="confirm-stop-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction
                    className="bg-[#FF5C6C] hover:bg-[#FF7080] text-[#0B0F14]"
                    onClick={onConfirm}
                    data-testid="bot-stop-confirm-button"
                >
                    Stop Bot
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
);

export const ConfirmLiveDialog = ({ open, onOpenChange, onConfirm, credsPresent, useTestnet, bot = "main" }) => {
    const [text, setText] = React.useState("");
    const [code, setCode] = React.useState("");
    const [sending, setSending] = React.useState(false);
    const [verifying, setVerifying] = React.useState(false);
    const [sentTo, setSentTo] = React.useState("");
    const requestedRef = React.useRef(false);
    const credsOk = credsPresent !== false;

    React.useEffect(() => {
        if (!open) {
            setText(""); setCode(""); setSentTo(""); requestedRef.current = false;
            return;
        }
        if (requestedRef.current || !credsOk) return;
        requestedRef.current = true;
        setSending(true);
        requestLiveOtp(bot)
            .then((r) => {
                setSentTo(r?.to || "");
                if (r?.emailed === false) {
                    toast.warning("Email unavailable — code written to the server log",
                        { description: r?.note || "Read it via journalctl / docker logs" });
                } else {
                    toast.success("Verification code emailed", { description: r?.to ? `Sent to ${r.to}` : undefined });
                }
            })
            .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to send code"))
            .finally(() => setSending(false));
    }, [open, credsOk, bot]);

    const resend = async () => {
        setSending(true);
        try { const r = await requestLiveOtp(bot); setSentTo(r?.to || ""); toast.success("New code emailed"); }
        catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Resend failed"); }
        finally { setSending(false); }
    };

    const confirm = async () => {
        setVerifying(true);
        try {
            await verifyLiveOtp(bot, code);
            await onConfirm();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Verification failed");
        } finally { setVerifying(false); }
    };

    const canConfirm = text === "LIVE" && code.length === 6 && credsOk && !verifying;
    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent className="panel" data-testid="confirm-live-dialog">
                <AlertDialogHeader>
                    <AlertDialogTitle className="text-[#FFB020]">Switch to LIVE trading?</AlertDialogTitle>
                    <AlertDialogDescription>
                        LIVE mode will route every order to AsterDex Spot V3 with REAL FUNDS
                        ({useTestnet ? "testnet" : "mainnet"}). The bot will ONLY ever touch
                        orders it created itself and never modify pre-existing orders on your wallet.
                        {!credsOk && (
                            <span className="block mt-2 text-[#FF5C6C]">
                                AsterDex credentials are not configured. Please add
                                ASTER_SIGNER_ADDRESS + ASTER_PRIVATE_KEY in <code>/app/backend/.env</code> first.
                            </span>
                        )}
                    </AlertDialogDescription>
                </AlertDialogHeader>

                <div className="text-xs text-[#A7B4C6] mt-3 mb-1">
                    Type <code className="text-[#FFB020] font-mono">LIVE</code> to confirm:
                </div>
                <input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    className="w-full bg-[#0B0F14] border border-[#1B2A3A] rounded-md px-3 py-2 text-sm font-mono text-[#E6EDF6] focus:outline-none focus:border-[#FFB020]"
                    placeholder="LIVE"
                    data-testid="confirm-live-input"
                />

                <div className="text-xs text-[#A7B4C6] mt-4 mb-1.5">
                    Enter the 6-digit code emailed{sentTo ? ` to ${sentTo}` : ""}:
                </div>
                <div data-testid="live-otp-input">
                    <InputOTP maxLength={6} value={code} onChange={setCode} disabled={!credsOk || verifying}>
                        <InputOTPGroup>
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                            <InputOTPSlot index={2} />
                            <InputOTPSlot index={3} />
                            <InputOTPSlot index={4} />
                            <InputOTPSlot index={5} />
                        </InputOTPGroup>
                    </InputOTP>
                </div>
                <button
                    type="button"
                    onClick={resend}
                    disabled={sending || !credsOk}
                    className="mt-2 text-[11px] text-[#2EE6D6] hover:underline disabled:opacity-40"
                    data-testid="live-otp-resend-button"
                >
                    {sending ? "Sending…" : "Resend code"}
                </button>

                <AlertDialogFooter>
                    <AlertDialogCancel data-testid="confirm-live-cancel-button">Cancel</AlertDialogCancel>
                    <button
                        type="button"
                        disabled={!canConfirm}
                        className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium bg-[#FFB020] hover:bg-[#FFC242] text-[#0B0F14] disabled:opacity-40 disabled:cursor-not-allowed"
                        onClick={confirm}
                        data-testid="mode-live-confirm-button"
                    >
                        {verifying ? "Verifying…" : "Verify & Enable LIVE"}
                    </button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

export const ConfirmPaperDialog = ({ open, onOpenChange, onConfirm, isRunning }) => (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent className="panel" data-testid="confirm-paper-dialog">
            <AlertDialogHeader>
                <AlertDialogTitle>Switch to PAPER mode?</AlertDialogTitle>
                <AlertDialogDescription>
                    The bot will stop routing orders to AsterDex with real funds and
                    switch to the simulated paper exchange (using real mainnet prices).
                    {isRunning && (
                        <span className="block mt-2 text-[#FFB020]">
                            The bot is currently running in LIVE mode. Existing LIVE orders
                            already placed on the exchange will remain open — only new
                            activity will be simulated after switching.
                        </span>
                    )}
                </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogCancel data-testid="confirm-paper-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction
                    className="bg-[#3DA7FF] hover:bg-[#5EB8FF] text-[#0B0F14]"
                    onClick={onConfirm}
                    data-testid="mode-paper-confirm-button"
                >
                    Switch to PAPER
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
);

export const ConfirmNetworkDialog = ({ open, onOpenChange, onConfirm, targetNetwork }) => {
    const isMainnet = targetNetwork === "mainnet";
    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent className="panel" data-testid="confirm-network-dialog">
                <AlertDialogHeader>
                    <AlertDialogTitle className={isMainnet ? "text-[#FFB020]" : "text-[#3DA7FF]"}>
                        Switch trading endpoint to {isMainnet ? "MAINNET" : "TESTNET"}?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                        {isMainnet ? (
                            <>
                                Signed orders will route to <code>https://sapi.asterdex.com</code>.
                                Make sure the API wallet credentials in <code>.env</code> are valid
                                for the mainnet wallet. Market data already uses mainnet.
                            </>
                        ) : (
                            <>
                                Signed orders will route to <code>https://sapi.asterdex-testnet.com</code>.
                                BTCUSDT is not listed on testnet — paper mode (default) lets you
                                validate the strategy against real BTCUSDT mainnet prices safely.
                            </>
                        )}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel data-testid="confirm-network-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                        className={isMainnet
                            ? "bg-[#FFB020] hover:bg-[#FFC242] text-[#0B0F14]"
                            : "bg-[#3DA7FF] hover:bg-[#5EB8FF] text-[#0B0F14]"}
                        onClick={onConfirm}
                        data-testid="confirm-network-confirm-button"
                    >
                        Switch to {isMainnet ? "MAINNET" : "TESTNET"}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};
